(ns graph-ui.web.render
  "Render-tree → reagent hiccup walk (PLAN-graph-ui-v0-001-02 Phase W2).

   Consumes the same renderer-agnostic render tree from ui.interpret/render-tree
   that the TUI consumes (ui.render.tui/draw-ops). Each {:type :props :children}
   node maps to one reagent component from graph-ui.web.widgets.

   This is the Web renderer's equivalent of the TUI's draw-ops + paint! — but
   CSS flexbox replaces manual layout, and React handles the diff/paint cycle."
  (:require [graph-ui.web.widgets :as widgets]
            [graph-ui.web.file-drop :as file-drop]
            [graph-ui.web.prompt-area :as prompt-area]
            [graph-ui.web.island-view :as island-view]
            [graph-ui.web.graph :as graph]
            [graph-ui.web.work-item :as work-item]
            [graph-ui.web.backlog :as backlog]
            [graph-ui.web.your-work :as your-work]
            [graph-ui.web.tracker-board :as tracker-board]
            [graph-ui.web.mantine-generic :as generic]
            [graph-ui.web.responsive :as responsive]
            [graph-ui.web.voice :as voice]
            [graph-ui.web.xray :as xray]
            [graph-ui.web.lazy :as lazy]
            [reagent.core :as r]))

;; @feature F-gi-v3-010-01-graph-ui-poc
;; @adr ADR-graph-ui-v0-003-01-ui-from-formal-description

(declare render-node* render-node**)

(defn- stalled-slot
  "The waiting mark a widget draws while its :on-visible source answers
   (PLAN-ikc-v3-033-01 step 4a).

   IT ASKS ON MOUNT, AND AGAIN WHEN ITS PAIR CHANGES. graph-ui.web.lazy records
   the pair and graph-ui.web.core reads it. A render must have no side effect of
   its own, so the ask lives in the lifecycle and the record is idempotent.

   THE SECOND ASK IS NEW (2026-09-25, lane RP). A widget with two deferred binds
   waits for one pair, and then for the next. React keeps THIS component when
   the pair changes, because it is the same kind in the same place. So an ask
   made only at mount asked for the first pair and never for the second. The
   conversation body waited on :page-conversation for ever, measured on a probe
   bus, and its mark still named the pair that had arrived.

   IT NEVER DRAWS NOTHING. A widget that drew empty here would say 'there is
   none of this', which is the one answer it does not have."
  [_pair]
  (r/create-class
   {:display-name "stalled-slot"
    :component-did-mount
    (fn [this] (let [{:keys [source args]} (second (r/argv this))]
                 (lazy/want! source args)))
    :component-did-update
    (fn [this old-argv]
      (let [pair (second (r/argv this))]
        (when (not= pair (second old-argv))
          (lazy/want! (:source pair) (:args pair)))))
    :reagent-render
    (fn [{:keys [source]}]
      [:div {:data-stalled (str source)
             :class "ikc-stalled"
             :style {:padding "var(--ikc-space-2, 8px)"
                     :color "var(--ikc-fg-muted, #888)"
                     :font-size "0.85em"}}
       "…"])}))

(defn render-node
  "Walk a render-tree node → reagent hiccup vector.
   Dispatches on :type to the matching widget component (via render-node*),
   then wraps the result in a flex-item div when the node carries :layout-self
   (ADR-graph-ui-v0-008-01).
   `bus` is the JS bus client object (for :input/:tree :on-submit dispatch);
   `session-id` is this view's own session (for :tree's $session-id fill —
   see widgets/widget-tree).
   `instrument?` (Studio Designer link-select, F-graph-ui-v0-005-01) stamps each
   node's DOM with a `data-widget-id` so a preview click can be traced back to the
   node — OFF by default, so the normal app render is byte-identical. The id lives
   on the SAME wrapper as :layout-self (or a fill-safe wrapper when there's no
   :layout-self), so instrumentation never regresses the fill chain.
   `selected-id` (optional, Studio Designer only) — when non-nil and matching a
   node's :widget-id, the wrapper gets a highlight outline so the Live preview
   visually marks the widget the tree / Selected-widget panel is pointing at.

   X-RAY (F-graph-ui-v0-010-01) reads graph-ui.web.xray directly rather than
   taking a sixth positional argument, for two reasons: the mode is one live
   global for the whole window — there is no rendering in which half the tree is
   x-rayed — and reading a reagent atom HERE is what makes the toggle reactive
   with no other wiring, including inside the nested :island-view previews that
   call this fn from a namespace that cannot require this one. It reuses the same
   wrapper the id needs, so it adds no DOM level that ?instrument=1 does not; it
   deliberately does NOT stamp `data-widget-id`, which stays the fidelity join's
   own seam (F-graph-ui-v0-008-01) and would otherwise start appearing in
   measurements nobody asked to instrument."
  ([bus session-id node] (render-node bus session-id node false nil))
  ([bus session-id node instrument?] (render-node bus session-id node instrument? nil))
  ([bus session-id {:keys [type props] :as node} instrument? selected-id]
   ;; :narrow — THE PHONE, and the one place the whole tree honours it
   ;; (dsl/contexts/ui.edn v2.0-SNAPSHOT :universal-slots :narrow,
   ;; PLAN-ikc-v3-015-01 phase 6). It sits HERE, beside :flex, because both are
   ;; statements a CHILD makes about its place in its parent, and because the
   ;; wrapper it needs is the wrapper that already exists.
   ;;
   ;; :hide RETURNS NOTHING AT ALL rather than a hidden box, for the reason
   ;; :collapsible's :hide-when-empty is decided here too: this is the last place
   ;; the resolved description exists, and a display:none box still costs the
   ;; phone its layout and its subtree's renders.
   ;;
   ;; Both rules are no-ops on a wide window, so the ordinary desktop render
   ;; stays byte-identical — the same contract instrument? keeps.
   (let [narrow  (:narrow props)
         narrow? (responsive/narrow?)]
     (when-not (responsive/hidden? narrow narrow?)
       (let [scroll (responsive/scroll-class narrow narrow?)
             body   (render-node** bus session-id node instrument? selected-id)]
         (if scroll [:div {:class scroll} body] body))))))

(defn- render-node**
  "render-node's wrapper half, unchanged, split out so :narrow can decide
   whether it runs at all."
  ([bus session-id {:keys [type props] :as node} instrument? selected-id]
   (let [hiccup (render-node* bus session-id node instrument? selected-id)
         ls     (:layout-self props)
         fx     (:flex props)   ; ADR-graph-ui-v0-013-01 §4 — v2.0's renamed :layout-self
         wid    (when instrument? (:widget-id props))
         xr?    (xray/on?)
         sel?   (and wid selected-id (= (str wid) (str selected-id)))
         ;; EVERY ROOT NAMES ITSELF (PLAN-ikc-v3-028-01 Phase D): the declared
         ;; :import-id rides on the wrapper as data-import-id, so a :coach-mark
         ;; can find the control a guide step points at. A node with no other
         ;; wrapper gets the same layout-neutral one x-ray uses (display:
         ;; contents), so the fill chain is what it was.
         iid    (some-> (:import-id props) str not-empty)
         ;; A wrapper that exists ONLY to carry x-ray's stamp generates NO BOX.
         ;;
         ;; George, 2026-08-18: in Markdown mode the body's left margin moved 62ch
         ;; -> 68ch and `editable-right-gutter` went visibly narrow. Cause: the
         ;; fill-safe wrapper below breaks `:container`'s MEASURE. widget-container
         ;; renders `w-full min-h-0 flex flex-col` plus Mantine's size — that is
         ;; `width:100%; max-width:15ch; margin-inline:auto` — and the wrapper
         ;; gives it no definite width to resolve `width:100%` against, so the box
         ;; collapses to its content: 15ch declared, 4.6ch measured. Mantine's
         ;; Container also centres with `margin-inline:auto`, which is what moved
         ;; the reading column's left margin.
         ;;
         ;; `display: contents` makes the element generate no box at all: its child
         ;; participates in the parent's layout exactly as if this div were absent.
         ;; So x-ray cannot move anything BY CONSTRUCTION, rather than by a width
         ;; that happens to work — and the DOM node still exists to carry the name
         ;; and be found by querySelectorAll.
         ;;
         ;; ONLY when x-ray is the sole reason for the wrapper. `:layout-self` and
         ;; `:flex` wrappers are load-bearing, and `?instrument=1`'s wrapper must
         ;; keep its box because ui.fidelity measures rects THROUGH data-widget-id
         ;; (F-graph-ui-v0-008-01) — a boxless element measures as 0x0 and would
         ;; silently empty that comparison.
         marker? (and (or xr? iid) (not ls) (not fx) (not wid))]
     (if (or ls fx wid xr? iid)
       [:div (cond-> {:class (str (cond
                                    ls (widgets/layout-self-class ls)
                                    fx (widgets/flex-class fx)
                                    ;; no self-sizing hint — a fill-safe wrapper JUST to
                                    ;; host the id, matching layout-self-class/flex-class's
                                    ;; own fill contract so the wrapped widget still grows.
                                    :else "flex flex-col min-h-0 min-w-0 [&>*]:grow [&>*]:min-h-0 [&>*]:min-w-0")
                                  (when sel? " outline outline-2 outline-offset-[-2px] [outline-color:var(--accent)]"))}
               marker? (-> (assoc :style {:display "contents"})
                           ;; The classes would be inert under display:contents,
                           ;; but leaving them is a lie about what this div does.
                           (dissoc :class))
               iid (assoc :data-import-id iid)
               wid (assoc :data-widget-id (str wid))
               ;; PURE DATA, styled by nothing. xray/overlay finds these, measures
               ;; their rects and draws in its own fixed layer, so the stamp adds
               ;; no appearance to the inspected element at all.
               ;;
               ;; `:data-xray-detail`, NOT `:title` — a title here was the second
               ;; version and it shadowed every widget's own tooltip while the
               ;; instrument was on, so hovering "New note" explained the wrapper
               ;; instead of the button. The overlay shows the same text in a
               ;; corner panel that covers nothing.
               xr? (assoc :data-xray-name   (xray/element-name props type)
                          :data-xray-detail (xray/element-detail props type)))
        hiccup]
       hiccup))))

(defn- render-node*
  "The bare widget dispatch, without the :layout-self/id wrapper render-node adds.
   `instrument?` and `selected-id` are threaded to child renders so the whole
   previewed subtree is stamped/highlighted, not just its root."
  [bus session-id {:keys [type props children]} instrument? selected-id]
  (if-let [pair (:stalled props)]
    ;; A BIND THAT SAYS :on-visible, DRAWN BEFORE ITS VALUE ARRIVED (step 4a).
    ;; The widget is not drawn empty. It draws a waiting mark, and it asks for
    ;; its value the moment it mounts. STALL, NEVER VANISH.
    [stalled-slot pair]
    (let [props (dissoc props :stalled)
          kid   (fn [c] (render-node bus session-id c instrument? selected-id))]
      (case type
      ;; The bus, so a box with :on-gesture :drop can send its command
      ;; (IKC3/PLAN-ikc-v3-064-01/DRAG).
      :box         [widgets/widget-box props (mapv kid (or children [])) bus session-id]
      :text        [widgets/widget-text props]
      :list        [widgets/widget-list props]
      :tree        [widgets/widget-tree props bus session-id]
      :input       [widgets/widget-input props bus session-id]
      :text-area   [widgets/widget-text-area props bus session-id]
      :prompt-area [prompt-area/widget-prompt-area props bus session-id]
      ;; PLAN-ikc-v3-015-01 phase 6. A case of its own, not a Mantine-backed
      ;; generic, because it owns two pieces of browser state a generic adapter
      ;; cannot give: whether it is listening, and whether it is speaking.
      :voice       [voice/widget-voice props bus session-id]
      ;; IKC3/PLAN-ikc-v3-061-01/UP-2. A case of its own: it owns the bytes of a
      ;; dropped file, the queue that sends them, and the refused rows.
      :file-drop   [file-drop/widget-file-drop props bus session-id]
      :code-block  [widgets/widget-code-block props bus session-id]
      :code-view   [widgets/widget-code-view props bus session-id]
      :markdown    [widgets/widget-markdown props bus session-id]
      ;; GRADUATED FROM GENERIC (PLAN-ikc-v3-017-01 phase 11). The generated
      ;; declaration's own words: a kind that needs data binding graduates to a
      ;; hand-written widget and an explicit case here.
      :image       [widgets/widget-image props bus session-id]
      :outline     [widgets/widget-outline props bus session-id]
      :button      [widgets/widget-button props bus session-id]
      ;; ABSENT RATHER THAN EMPTY, when the description asked for it
      ;; (F-platform-v0-014-01). Decided HERE and not inside widget-collapsible
      ;; because this is the last place the resolved DESCRIPTION subtree exists —
      ;; one step later the children are rendered hiccup and "does this hold
      ;; anything" would have to be read back out of markup.
      :collapsible (when-not (and (:hide-when-empty props)
                                  (widgets/content-empty? {:props props :children children}))
                     ;; A CLOSED FOLD DRAWS NOTHING INSIDE IT, and that is what
                     ;; makes :load :on-visible mean anything here
                     ;; (PLAN-ikc-v3-033-01 step 4b). Mantine's Collapse was
                     ;; handed its children whatever `:in` said, so a closed
                     ;; section still MOUNTED them — and a bind that waits for
                     ;; its widget to draw would have fired at first paint and
                     ;; deferred nothing. The four sidebar trees live in closed
                     ;; folds, and they are the 878 kB.
                     ;;
                     ;; THE RULE IS THE ONE :tab-set ALREADY USES, and so is the
                     ;; exception: a child that can hold unsaved text stays
                     ;; drawn, because a remount seeds its buffer from the last
                     ;; SAVED value and the typed part goes with nothing saying
                     ;; so. holds-draft? derives that from the subtree, so no
                     ;; island declares it and none can forget.
                     ;; EACH CHILD GOES AS [hiccup draft?], which is the shape
                     ;; :tab-set already takes. The hiccup is only a vector until
                     ;; something renders it, so a child the fold does not place
                     ;; never mounts and never asks for its source.
                     [widgets/widget-collapsible props
                      (mapv (fn [c] [(kid c) (widgets/holds-draft? c)])
                            (or children []))
                      bus session-id])
      ;; THE THIRD ELEMENT IS WHY THIS IS DECIDED HERE, for the same reason
      ;; :collapsible above is: this is the last place the resolved DESCRIPTION
      ;; subtree exists, and "can this tab be holding unsaved text" cannot be
      ;; read back out of rendered hiccup. A tab-set draws one child (every
      ;; version of the declaration says so), except a child whose renderer owns
      ;; an edit buffer — see widgets/holds-draft?.
      ;; THE TITLE IS THE TAB'S, and the panel does not draw it again
      ;; (widgets/tab-panel, fault report 20fee998).
      ;; A CHILD ON THE LINE OF THE TABS IS NOT A TAB (IKC3/PLAN-ikc-v3-068-01/T).
      ;; Split here for the same reason as :app-shell below: :section is on the
      ;; raw child and gone once the child is hiccup.
      :tab-set     (let [kids (or children [])
                         line (fn [s] (into [] (comp (filter #(= s (get-in % [:props :section])))
                                                     (map kid))
                                            kids))]
                     [widgets/widget-tab-set props
                      (into [] (comp (remove widgets/tab-line-child?)
                                     (map (fn [c] [(get-in c [:props :title]) (kid (widgets/tab-panel c))
                                                   (widgets/holds-draft? c)])))
                            kids)
                      {:left (line :left-section) :right (line :right-section)}])
      :split-pane  [widgets/widget-split-pane props (mapv kid (or children []))]
      :select      [widgets/widget-select props bus session-id]
      :table       [widgets/widget-table props bus session-id]
      ;; A BOOK'S READING SURFACE: one row per verse, four aligned cells.
      ;; bus + session: a dragged divider dispatches the reader's widths.
      :verse-rows  [widgets/widget-verse-rows props bus session-id]
      :menu        [widgets/widget-menu props bus session-id]
      ;; :activity-log reads the client activity ring-buffer directly (no bind,
      ;; no bus) — a pure client-side observability surface.
      :activity-log [widgets/widget-activity-log props]
      ;; :island-view renders ANOTHER island read-only; it needs to render a
      ;; subtree, so render-node is passed IN (island-view can't require this ns —
      ;; that would be a cycle).
      :island-view [island-view/widget-island-view props bus session-id render-node]
      ;; Meta-UI v2.0 (ADR-graph-ui-v0-013-01) — layout containers (§0/§3) and the
      ;; six new kinds (§11-18). New-at-v2.0, no v1.x consumer.
      ;; :dock — LEVEL 5 (ADR-graph-ui-v0-026-01). It is the one container that
      ;; needs the RAW children, not the rendered ones: a pane's tab label is
      ;; its child's :title and its panel id is that child's :import-id, and
      ;; both are gone once a node is hiccup. So the triple is built here and
      ;; the widget stays ignorant of the widget tree, which is what lets a
      ;; second renderer reuse it.
      ;; THE FOURTH ELEMENT IS THE PANE'S OWN PLACE — :direction and :width, the
      ;; :pane universal slot ui.patterns writes from the pattern's facts. It
      ;; travels beside the title for the same reason: it is on the raw child
      ;; and gone once the child is hiccup.
      :dock        (let [panes (map-indexed
                                 (fn [i c] [(widgets/dock-panel-id c i)
                                            (widgets/dock-panel-title c i)
                                            (kid c)
                                            (widgets/dock-panel-pane c)])
                                 (or children []))]
                     ;; THE TWO HALVES OF PHASE 2, HANDED IN AS DATA.
                     ;; :arrangement is what the bound :dock-arrangement source
                     ;; answered — the person's own layout and the panes it was
                     ;; made for. on-moved sends a MOVE back, and the widget
                     ;; calls it only for :origin user.
                     ;; A PHONE GETS ONE PANE AT A TIME (bug 3 of
                     ;; IKC3-Bugs-20260928). dockview lays every pane side by
                     ;; side, and below --viewport-narrow that left the reading
                     ;; pane 100 pixels wide on every pattern island. The
                     ;; stored arrangement is not read or written there.
                     (if (responsive/narrow?)
                       [widgets/widget-dock-narrow props (vec panes)]
                     [widgets/widget-dock
                      (assoc props
                             :arrangement-in (:arrangement props)
                             :on-moved
                             (when bus
                               (fn [json stamp]
                                 ;; The same call every widget's gesture makes
                                 ;; (widgets.cljs js-invoke bus "dispatch").
                                 (js-invoke bus "dispatch"
                                   {:type        :ui/set-dock-arrangement
                                    :session-id  session-id
                                    :arrangement json
                                    :for-panes   stamp}
                                   (fn [_] nil)))))
                      (vec panes)]))
      :stack       [widgets/widget-stack props (mapv kid (or children []))]
      :group       [widgets/widget-group props (mapv kid (or children []))]
      :flex        [widgets/widget-flex props (mapv kid (or children []))]
      :grid        [widgets/widget-grid props (mapv kid (or children []))]
      :grid-col    [widgets/widget-grid-col props (mapv kid (or children []))]
      :simple-grid [widgets/widget-simple-grid props (mapv kid (or children []))]
      ;; ADR-graph-ui-v0-013-01 §20/§22 (2026-07-28) — the centred reading column,
      ;; and "what scrolls" as something a description can SAY.
      ;; ADR-graph-ui-v0-001-01 §B — a page, with its sections NAMED by each
      ;; child's :section. Grouped here rather than in the widget because
      ;; :section belongs to the NODE and is gone once a child is hiccup.
      ;; Children with no :section land in :main, so a description that says
      ;; nothing gets the sensible thing rather than nothing.
      ;; :section lives in a child's :props, not on the child. A RENDER-tree
      ;; node is not a description node — it dispatches on :type and carries
      ;; every slot under :props, which is how :flex is read a few lines above.
      ;; Grouping on (:section child) silently put every child in :main: the
      ;; page reserved the navbar's width and then rendered the navbar's content
      ;; beside the empty gutter. Nothing threw, and the probe found every
      ;; button present.
      :app-shell   [widgets/widget-app-shell props
                    (reduce-kv (fn [m k vs] (assoc m k (mapv kid vs)))
                               {}
                               (group-by #(or (get-in % [:props :section]) :main)
                                         (or children [])))]
      :container   [widgets/widget-container props (mapv kid (or children []))]
      :scroll-area [widgets/widget-scroll-area props (mapv kid (or children []))]
      :icon        [widgets/widget-icon props]
      :icon-button [widgets/widget-icon-button props bus session-id]
      :badge       [widgets/widget-badge props]
      ;; :highlight-list, NOT :highlight — that kind is the generated Mantine
      ;; substring-highlighter and falls through to mantine-generic below
      ;; (F-platform-v0-011-01, ADR-graph-ui-v0-017-01 §5).
      :highlight-list [widgets/widget-highlight-list props bus session-id]
      :chip        [widgets/widget-chip props bus session-id]
      :coach-mark  [widgets/widget-coach-mark props bus session-id]
      :drawer      [widgets/widget-drawer props bus session-id (mapv kid (or children []))]
      :color-input [widgets/widget-color-input props bus session-id]
      ;; GRADUATED FROM GENERIC (PLAN-ikc-v3-016-02 T5). The generated kind had
      ;; 33 props and no data binding; this one binds the page's tags and the tag
      ;; vocabulary, and turns Mantine's whole-array change into the per-tag
      ;; gestures the two knowledge-graph commands take.
      :tags-input  [widgets/widget-tags-input props bus session-id]
      ;; THE GRAPH, DRAWN AS A GRAPH (PLAN-ikc-v3-024-01 M8 O10, track R).
      ;; Its own namespace and not widgets.cljs: it carries three layout
      ;; algorithms and an SVG renderer, which is the size at which
      ;; prompt_area, island_view and voice each moved out too.
      :graph       [graph/widget-graph props bus session-id]
      ;; THE WORK ITEM PAGE (IKC3/PLAN-ikc-v3-080-01/ITEM). Five product kinds,
      ;; in their own namespace for the reason :graph gives above.
      :inline-edit       [work-item/widget-inline-edit props bus session-id]
      :field-list        [work-item/widget-field-list props bus session-id]
      :criteria-list     [work-item/widget-criteria-list props bus session-id]
      :status-menu       [work-item/widget-status-menu props bus session-id]
      :development-panel [work-item/widget-development-panel props bus session-id]
      ;; THE BACKLOG (IKC3/PLAN-ikc-v3-080-01/BACKLOG). One band of screen 2.
      :backlog-band      [backlog/widget-backlog-band props bus session-id]
      ;; YOUR WORK AND IN PROGRESS (IKC3/PLAN-ikc-v3-080-01/ME). Screens 8 and 9.
      :your-work-group   [your-work/widget-your-work-group props bus session-id]
      :in-progress-list  [your-work/widget-in-progress-list props bus session-id]
      ;; THE BOARD OF THE TRACKER (IKC3/PLAN-ikc-v3-080-01/BOARD). Five product
      ;; kinds, in their own namespace for the reason :graph gives above. A
      ;; :swimlane is a container: its children are its :board-column nodes.
      :work-card       [tracker-board/widget-work-card props bus session-id]
      :board-column    [tracker-board/widget-board-column props bus session-id]
      :swimlane        [tracker-board/widget-swimlane props (mapv kid (or children []))]
      :status-chain    [tracker-board/widget-status-chain props]
      :tracker-sidebar [tracker-board/widget-tracker-sidebar props]
      :report-button   [tracker-board/widget-report-button props bus session-id]
      ;; Every OTHER kind in the vocabulary is Mantine-backed and GENERATED
      ;; (F-graph-ui-v0-006-01), so it needs no case of its own: the manifest
      ;; already records which Mantine component it is, which props are legal and
      ;; which children are named slots, and mantine-generic drives it from that.
      ;; A case above this line means the kind needed something a generic adapter
      ;; cannot give — a data binding, an edit buffer, selection state — not that
      ;; it is special. Only a kind that is neither hand-written nor Mantine-backed
      ;; still falls through to the visible `unknown widget` placeholder.
      (generic/widget-generic
       type props
       (mapv (fn [c] {:node c :hiccup (kid c)}) (or children []))
       bus session-id)))))

(defn render-tree
  "Walk a full render tree → reagent hiccup (the root component).
   `tree` is {:type :props :children [...]} from ui.interpret/render-tree.
   `bus` is the JS bus client object (for :input/:tree :on-submit dispatch);
   `session-id` is this view's own session.

   `instrument?` (4-arity) stamps `data-widget-id` on every node — the same flag
   the Studio preview uses for click-to-source, exposed to the TOP-LEVEL app so
   the browser rendering can be MEASURED (F-graph-ui-v0-008-01). The browser is
   the reference rendering a Penpot board is judged against, and a reference you
   cannot key by widget-id can only be compared by eye. Off by default, so the
   ordinary render stays byte-identical."
  ([tree bus session-id] (render-tree tree bus session-id false))
  ([tree bus session-id instrument?]
  ;; The renderer frame is NEUTRAL: it themes its ground through the tokens (the
  ;; old hardcoded ikc-bg-ground was a pre-ADR-graph-ui-v0-009 leftover that painted a dark
  ;; frame under amber) and adds NO padding of its own — a full-bleed island
  ;; (notes) owns its own edges; the p-2 gutter was the stray inset George saw.
  ;; NOTE (2026-07-14): a `min-h-0` here (to give descendants' overflow-auto a
  ;; bounded box to scroll within) REGRESSED content visibility — the content
  ;; pane collapsed to nothing against the real vault. Reverted to the known-good
  ;; grow-to-content regime (content visible, long pages don't scroll yet); the
  ;; scroll fix must be re-approached surgically on the content pane's own flex
  ;; chain, headlessly reproduced first, not on this shared root.
  [:div.flex-1.flex.flex-col.overflow-hidden
   (cond-> {:style {:background "var(--ground)"}}
     instrument? (assoc :data-render-root "1")
     ;; X-RAY's one switch. Every rule in ikc-base.css hangs off this attribute,
     ;; so the whole instrument is off — not merely invisible — whenever it is
     ;; absent, and no stylesheet rule can reach a node without it.
     (xray/mode) (assoc :data-xray (name (xray/mode))))
   [render-node bus session-id tree instrument?]]))
